import { Mail, Phone, Star } from 'lucide-react';

import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';
import { formatPhoneDisplay } from '@/lib/contacts/phone';

interface RailCompany {
  name?: string | null;
  logoUrl?: string | null;
  logoDataUri?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  } | null;
  taxRegistrationNumber?: string | null;
  /** Marketing line under the name, e.g. "Heating · Cooling · Plumbing". */
  tagline?: string | null;
  /** Contractor/trade license number(s) — the rail's "License" line. */
  contractorLicense?: string | null;
  regulatoryNotice?: string | null;
}

/**
 * The persistent left rail on the single-scope estimate — the tenant's identity
 * and how to reach them, mirroring the artifact. Every line is data-gated: no
 * phone, address, license, or reviews on file simply omits that line rather than
 * printing an empty label. Sticky on large screens; stacks above the document on
 * small ones.
 */
export function SingleScopeCompanyRail({
  company,
  reviews,
}: {
  company?: RailCompany | null;
  reviews?: { averageRating: number; count: number } | null;
}) {
  const name = company?.name?.trim() || 'Your provider';
  const logo = company?.logoDataUri || company?.logoUrl || null;
  const phone = company?.phone?.trim();
  const email = company?.email?.trim();
  const addr = company?.address ?? null;
  const addressLines = addr
    ? [addr.street, [addr.city, addr.state].filter(Boolean).join(', '), addr.zip]
        .map((l) => l?.trim())
        .filter((l): l is string => Boolean(l))
    : [];
  const tagline = company?.tagline?.trim();
  // The rail's license line is the contractor/trade license, distinct from the
  // tax-registration number (which surfaces as "Tax #" on the document, not here).
  const license = company?.contractorLicense?.trim();

  return (
    // The artifact's .pubrail: a full-height sticky column that flex-anchors its
    // contact/reviews/attribution to the bottom, with the brand + license at top.
    <aside className="hidden shrink-0 flex-col self-start border-[#14141A]/[0.08] lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[268px] lg:border-r lg:px-[26px] lg:pb-5 lg:pt-[30px]">
      {logo ? (
        <img src={logo} alt={name} className="size-[92px] shrink-0 rounded-[20px] object-contain" />
      ) : (
        <span className="flex size-[92px] shrink-0 items-center justify-center rounded-[20px] bg-[#17171A] text-[30px] font-medium text-white">
          {name.charAt(0).toUpperCase()}
        </span>
      )}
      <p className="mt-4 text-[19px] font-medium leading-tight tracking-[-0.025em] text-[#17171A]">
        {name}
      </p>
      {tagline ? (
        <p className="mt-1.5 text-[12.5px] font-medium text-[var(--ss-accent-strong)]">{tagline}</p>
      ) : null}
      {license ? <p className="mt-2 text-[12px] text-[#95969D]">License {license}</p> : null}
      {company?.regulatoryNotice ? (
        <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed">
          {company.regulatoryNotice}
        </p>
      ) : null}

      {/* Spacer — pushes the contact/reviews/attribution stack to the bottom on
          large screens; collapses to nothing when the rail is stacked on mobile. */}
      <div className="hidden lg:block lg:flex-1" />

      {phone || email || addressLines.length > 0 ? (
        <div className="mt-6">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D]">
            Contact us
          </p>
          <div className="mt-2 space-y-3 text-[13.5px]">
            {phone ? (
              <a
                href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                className="flex items-center gap-2.5 text-[#33343A] hover:underline"
              >
                <Phone className="size-4 shrink-0 text-[#95969D]" />
                {formatPhoneDisplay(phone)}
              </a>
            ) : null}
            {email ? (
              <a
                href={`mailto:${email}`}
                className="flex items-center gap-2.5 break-all text-[#33343A] hover:underline"
              >
                <Mail className="size-4 shrink-0 text-[#95969D]" />
                {email}
              </a>
            ) : null}
            {addressLines.length > 0 ? (
              <p className="text-[#63646B]">{addressLines.join(', ')}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {reviews && reviews.count > 0 ? (
        <div className="mt-[18px] border-t border-[#14141A]/[0.08] pt-[15px]">
          <div className="flex items-center gap-0.5" aria-hidden="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={
                  i < Math.round(reviews.averageRating)
                    ? 'size-3.5 fill-[#D99A2B] text-[#D99A2B]'
                    : 'size-3.5 fill-gray-200 text-gray-200'
                }
              />
            ))}
          </div>
          <p className="mt-1.5 text-[12px] text-[#95969D]">
            <span className="font-medium text-[#17171A]">{reviews.averageRating.toFixed(1)}</span> ·{' '}
            {reviews.count} Google reviews
          </p>
        </div>
      ) : null}

      <div className="mt-5 hidden border-t border-[#14141A]/[0.08] pt-4 lg:block">
        <PoweredByNotifi />
      </div>
    </aside>
  );
}
